import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import CustomDialog from './CustomDialog';
import {
  Box,
  Stepper,
  Step,
  Stack,
  FormControlLabel,
  RadioGroup,
  Radio
} from '@mui/material';
import CustomText from 'components/common/CustomText/CustomText';
import Payment from '../../common/payPal/PayPal';
import * as api from 'services/oldApiService';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import { useGetCurrentPlan } from 'hooks/usePlans';
import { colors } from 'styles/colors';
import { fonts } from 'styles/fonts';
import { useGetPlans } from 'hooks/useLookups';
import { toTitleCase } from 'common/utils';
import { FormProvider, useForm } from 'react-hook-form';
import CustomFormSelect from 'components/inputs/CustomFormSelect/CustomFormSelect';
import { EPlanPeriod } from 'enum/EPlanPeriod';
import CustomFormTextInput from 'components/inputs/CustomFormTextInput/CustomFormTextInput';
import { useValidateCoupon } from 'hooks/useCoupon';
import { useCreateSubscription, useUpdateSubscription } from 'hooks/useSubscription';
import PlanIcon from 'components/common/PlanIcon/PlanIcon';
import useGlobalStyles from 'styles/globalStyles';
import { snackbarService } from 'services/snackbarService';
import CancelMembershipDialog from './CancelMembershipDialog';
import { useNavigate } from 'react-router-dom';

interface Props {
  open: boolean;
  setOpen: (value: boolean) => void;
  refetch?: () => void;
}

const steps = ['Select Plan', 'Payment'];

export default function UpgradeDialog({ open, setOpen, refetch }: Props) {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState<string | undefined>();
  const [userToken, setUserToken] = useState<string>('');
  const [customerId, setCustomerId] = useState<string>('');
  const [period, setPeriod] = useState<EPlanPeriod | null>(null);
  const [coupon, setCoupon] = useState<any>(null);
  const globalStyles = useGlobalStyles();
  const [cancelMembershipOpen, setCancelMembershipOpen] = useState<boolean>(false);

  const { mutate: createMutation, isLoading: isCreating } = useCreateSubscription(
    (res: any) => {
      if (res?.subscription_id) {
        setOpen(false);
        snackbarService.showSnackbar('Your subscription has been successfully updated.', 'success');
        refetch?.();
      } else {
        snackbarService.showSnackbar(res?.message, 'error');
      }
    },
    (err: any) => {
      snackbarService.showSnackbar(err?.message, 'error');
    }
  );

  const { mutate: updateMutation, isLoading: isUpdating } = useUpdateSubscription(
    (res: any) => {
      if (res?.subscription_id) {
        setOpen(false);
        snackbarService.showSnackbar('Your subscription has been successfully updated.', 'success');
        refetch?.();
      } else {
        snackbarService.showSnackbar(res?.message, 'error');
      }
    },
    (err: any) => {
      snackbarService.showSnackbar(err?.message, 'error');
    }
  );

  const methods = useForm({
    defaultValues: {
      plan_id: '',
      payment_method_nonce: '',
      promotion_code: '',
    }
  });

  const {
    handleSubmit,
    setValue,
    register,
    watch,
    getValues,
  } = methods;

  const code = watch('promotion_code');
  const planId = watch('plan_id');

  const { data: plan } = useGetCurrentPlan({
    refetchOnWindowFocus: false,
    onSuccess: (res: any) => {
      const currentPlanId = getValues('plan_id');

      if (!currentPlanId) {
        setValue('plan_id', res?.data?.braintree_plan_id);
        setPeriod(res?.data?.period?.toLowerCase());
      }
    }
  });

  const { data: plans } = useGetPlans();

  const selectedPlan = useMemo(() => {
    if (!plans || !planId || !period) return null;

    const list = period === EPlanPeriod.MONTHLY
      ? plans.monthly
      : plans.annual;

    return list?.find((p: any) => p.braintree_plan_id === planId);
  }, [plans, planId, period]);

  const price = selectedPlan?.cost ?? 0;

  const { data } = useValidateCoupon({
    code,
    price,
  }, {
    enabled: !!code && !!price,
    onSuccess: (res: any) => {
      if (res?.data?.valid) {
        setCoupon(res.data);
      } else {
        setCoupon(null);
      }
    }
  });

  const finalAmount = useMemo(() => {
    if (!selectedPlan) return 0;

    if (coupon?.valid) {
      return coupon.new_price;
    }

    return selectedPlan.cost;
  }, [selectedPlan, coupon]);


  const monthlyPlans = useMemo(() => {
    return plans?.monthly?.map((p: any) => ({
      key: p.braintree_plan_id,
      label: `${toTitleCase(p.plan.name)} ($${p.cost})`,
      value: p.braintree_plan_id,
      disabled: p.braintree_plan_id == plan?.data?.braintree_plan_id || p?.plan?.name == 'Free',
    }));
  }, [plans, plan]);

  const annualPlans = useMemo(() => {
    return plans?.annual?.map((p: any) => ({
      key: p.braintree_plan_id,
      label: `${toTitleCase(p.plan.name)} ($${p.cost})`,
      value: p.braintree_plan_id,
      disabled: p.braintree_plan_id == plan?.data?.braintree_plan_id || p?.plan?.name == 'Free',
    }));
  }, [plans, plan]);

  const getUserToken = useCallback(async () => {
    const { data, status_code: statusCode } = await api.getUserToken();
    if (statusCode === api.status.SUCCESS) {
      setUserToken(data.token);
      setCustomerId(data.customer_id);
    }
  }, []);

  const onSubmit = (values: any) => {
    handleNext();
  };

  const openCancelMembershipDialog = () => {
    setCancelMembershipOpen(true);
  }

  const goToAccountDeletion = () => {
    setOpen(false);
    navigate('/account-deletion');
  }

  useEffect(() => {
    if (!code) {
      setCoupon(null);
    }
  }, [code]);

  useEffect(() => {
    if (open) {
      getUserToken();
      setActiveStep(0);
    }
  }, [open, getUserToken]);

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  useEffect(() => {
    if (paymentMethod) {
      if (!plan?.data) {
        // Current plan hasn't loaded yet -- can't safely tell create from
        // update. Bail rather than guess or throw; the user can retry once
        // it's loaded.
        snackbarService.showSnackbar('Still loading your current plan, please try again in a moment.', 'error');
        return;
      }

      setValue('payment_method_nonce', paymentMethod);

      const values = {
        ...getValues(),
      }

      if (!coupon?.valid) {
        delete (values as any).promotion_code;
      }

      // A real Braintree subscription to update only exists if the current
      // provider is BRAINTREE -- Free-plan users AND anyone else without a
      // real Braintree subscr_id (e.g. an admin-granted/comp'd paid plan,
      // provider=NONE) must go through create, not update, since there's
      // nothing on Braintree's side for update_subscription to reference.
      if (plan.data.provider === 'BRAINTREE') {
        updateMutation({
          ...values,
          subscription_id: plan.data.subscr_id,
        });
      } else {
        createMutation(values);
      }
    }

  }, [paymentMethod]);

  /**
   * Prevent clearing plan on first API initialization
   */

  useEffect(() => {
    if (plan) {
      if (plan?.data?.period?.toLowerCase() == period) {
        setValue('plan_id', plan.data.braintree_plan_id);
      } else {
        setValue('plan_id', '');
      }
    }
  }, [period])

  const isAppleManaged = plan?.data?.provider === 'APPLE';

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>

            <Stack direction='row' spacing={0.5}>
              <CustomText
                text='Your current plan'
                color={colors.black}
              />
              <CustomText
                text={`(${plan?.data?.plan?.name})`}
                color={colors.warning}
              />
            </Stack>

            {isAppleManaged ? (
              <Box mt={3}>
                <CustomText
                  text='This subscription was purchased through the App Store. To upgrade, downgrade, or change your plan, please manage it in your App Store settings.'
                  color={colors.gray}
                />
              </Box>
            ) : (
              <>
                <Stack direction="row" spacing={0} mt={3}>
                  <RadioGroup
                    row
                    value={period}
                    sx={{ width: '100%', gap: 1 }}
                    name='period'
                    onChange={(e) => setPeriod(e.target.value as EPlanPeriod)}
                  >
                    {[EPlanPeriod.MONTHLY, EPlanPeriod.ANNUAL].map((option) => (
                      <Box
                        key={option}
                        sx={{
                          flex: 1,
                          border: `1px solid ${colors.secondary}`,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          // padding: 1.5,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          borderRadius: '4px'
                        }}
                      >
                        <FormControlLabel
                          value={option}
                          control={
                            <Radio
                              sx={{
                                transform: 'scale(0.9)',
                                color: '#999999',
                                '&.Mui-checked': {
                                  color: '#ff9800',
                                },
                              }}
                            />
                          }
                          label={toTitleCase(option)}
                          sx={{
                            margin: 0,
                            fontSize: 20,
                            fontFamily: fonts.neue,
                          }}
                        />
                      </Box>
                    ))}
                  </RadioGroup>
                </Stack>

                {/* Plans Options */}
                <Box mt={2}>
                  <CustomFormSelect
                    name="plan_id"
                    options={period === EPlanPeriod.ANNUAL ? annualPlans : period === EPlanPeriod.MONTHLY ? monthlyPlans : []}
                    floatingLabel="Plan"
                    placeholder='Select plan'
                  />
                </Box>

                <Box mt={2}>
                  <CustomFormTextInput
                    name='promotion_code'
                    register={register}
                    placeholder='Promotion code'
                    floatingLabel='Promotion code'
                  />
                </Box>

                <Box mt={1}>
                  <CustomText
                    text={'Add your promotional code and get a discount on your subscription'}
                    color={colors.gray}
                    fontSize={12}
                  />
                </Box>

                <Box mt={3}>
                  <Stack
                    direction={'row'}
                    alignItems={'center'}
                    spacing={0.5}
                  >
                    <CustomText
                      text={`Total fee with the promotional code:`}
                      color='black'
                    />
                    <CustomText
                      text={`$${finalAmount.toFixed(2)}`}
                      color={colors.warning}
                    />
                  </Stack>
                </Box>

                {/* Continue button */}
                <Box my={2}>
                  <CustomButton
                    text='Continue'
                    fullWidth
                    type='submit'
                    disabled={selectedPlan?.braintree_plan_id === plan?.data?.braintree_plan_id}
                  />
                </Box>
              </>
            )}

            {/* Cancel button */}
            {plan?.data?.plan?.name !== 'Free' && <Box my={2}>
              <CustomButton
                text='Cancel membership'
                fullWidth
                intent='warning'
                variant='outlined'
                onClick={openCancelMembershipDialog}
              />
            </Box>}

            {/* Delete account button */}
            <Box my={2}>
              <CustomButton
                text='Delete Account'
                fullWidth
                intent='error'
                variant='outlined'
                onClick={goToAccountDeletion}
              />
            </Box>



          </Box>
        );

      case 1:
        return (
          <Box>

            {/* Selected Plan Details */}
            <Box mt={3}>
              <Box className={globalStyles.sectionContainer}>
                <Box
                  className={globalStyles.sectionHeader}
                  sx={{ position: 'relative' }}
                >
                  <Box>
                    <CustomText
                      text={selectedPlan?.plan?.name}
                      fontSize={18}
                      fontWeight={600}
                    />
                    <Stack
                      direction={'row'}
                      alignItems={'center'}
                      spacing={0.5}
                    >
                      <CustomText
                        text={`$${selectedPlan?.cost} / ${selectedPlan?.period}`}
                        sx={{
                          textDecoration: finalAmount < selectedPlan?.cost ? 'line-through' : 'none'
                        }}
                        fontSize={16}
                      />
                      {finalAmount < selectedPlan?.cost && <CustomText
                        text={`$${finalAmount.toFixed(2)}`}
                        fontSize={16}
                        color={colors.warning}
                      />}
                    </Stack>
                  </Box>

                  <PlanIcon planName={selectedPlan.plan.name} height={25} />
                </Box>
              </Box>
            </Box>

            <Box mt={2}>
              <Payment
                planId={selectedPlan.braintree_plan_id}
                setPaymentMethod={setPaymentMethod}
                clientToken={userToken}
                customerId={customerId}
                isCreating={isCreating}
                isUpdating={isUpdating}
              />
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <CustomDialog
        open={open}
        title="Manage Plans"
        alignText="center"
        onClose={() => setOpen(false)}
        content={
          <FormProvider {...methods}>
            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
            >
              <Box sx={{ width: '100%' }}>
                <Stepper activeStep={activeStep} connector={null} sx={{ mb: 4 }}>
                  {steps.map((label) => (
                    <Step key={label}></Step>
                  ))}
                </Stepper>

                {renderStepContent()}
              </Box>
            </Box>
          </FormProvider>
        }
      />

      <CancelMembershipDialog
        open={cancelMembershipOpen}
        setOpen={setCancelMembershipOpen}
        closeParent={() => setOpen(false)}
        refetch={refetch}
        provider={plan?.data?.provider}
      />
    </>
  );
}